import React, { useEffect, useCallback, useState } from "react";
import "./map.css";
import { colorScale } from "../../utils/utils";
import {
  HEATMAP_COLORS,
  HEATMAP_SCALE,
  KIOSK_DIMENSIONS,
} from "../../utils/consts";
import { useAppSelector } from "../../redux";
import { BuildingPopup } from "./BuildingPopup";
import { BUILDINGS } from "./heatmapConfig";
import { Building } from "../../components/Building";
import { MeterData } from "../../redux/rootReducer";
import useWindowDimensions from "../../hooks/useWindowDimensions";

export const MapScreen: React.FC = () => {
  const [selectedBuildingId, selectBuildingId] = useState<string | null>(null);
  const resetScreens = useAppSelector((state) => state.reset);
  const data = useAppSelector<MeterData>((state) => state.electric).data;
  const lastUpdated = useAppSelector((state) => state.electric.lastUpdated);

  // calculate building dimensions and location on current screen
  // based on values for kiosk screen
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const { width: kioskWidth, height: kioskHeight } = KIOSK_DIMENSIONS;

  const getSizeAndPosition = useCallback(
    (
      size: { width: number; height: number },
      position: { top: number; left: number }
    ) => {
      const { width, height } = size;
      const { top, left } = position;

      const widthScale = windowWidth / kioskWidth;
      const heightScale = windowHeight / kioskHeight;
      const areaScale = Math.sqrt(widthScale * heightScale);

      // scale size so it looks ok on this screen
      const newSize = {
        width: width * areaScale,
        height: height * areaScale,
      };

      // change position so it's visible on screen
      const newPosition = {
        top: top * heightScale,
        left: left * widthScale,
      };

      return { size: newSize, position: newPosition };
    },
    [kioskHeight, kioskWidth, windowHeight, windowWidth]
  );

  // calculate glow colors based on data
  const calculateColor = useCallback(
    (buildingId: string) => {
      // find points with this building id
      const filtered = data.filter((obj) => obj.id === buildingId);
      // no value, make it gray
      if (filtered.length === 0) return "#777777";
      const value = filtered.slice(-1)[0].curValue; // use latest value
      return colorScale(HEATMAP_COLORS, value, HEATMAP_SCALE);
    },
    [data]
  );

  const closePopup = () => selectBuildingId(null);

  useEffect(() => {
    closePopup();
  }, [resetScreens]);

  return (
    <div
      className="expand"
      style={{ position: "absolute", overflow: "hidden" }}
    >
      <BuildingPopup
        selectedBuildingId={selectedBuildingId}
        closePopup={closePopup}
      />

      <img
        src="../map_background.png"
        alt="campus map"
        width="100%"
        height="100%"
      />

      {/* building images */}
      {Object.entries(BUILDINGS).map(([id, building]) => {
        const { size: scaledSize, position: scaledPosition } =
          getSizeAndPosition(building.size, building.position);

        return (
          <div
            key={id}
            style={{
              position: "absolute",
              ...scaledPosition,
              ...scaledSize,
              cursor: "pointer",
            }}
            onClick={() => selectBuildingId(id)}
          >
            <Building
              name={building.imageName}
              glowColor={calculateColor(id)}
              size={scaledSize}
            ></Building>
          </div>
        );
      })}

      <div className="title-abs-container">
        <div className="title-container">
          {/* title */}
          <div className="title">Dartmouth Energy Kiosk</div>

          {/* Color Scale */}
          <div className="color-scale">
            <div
              className="color-scale-gradient"
              style={{
                background: `linear-gradient(to right, ${HEATMAP_COLORS.join(
                  ", "
                )})`,
              }}
            />
            <div className="color-scale-labels">
              <span>Low energy consumption</span>
              <span>High energy consumption</span>
            </div>
          </div>

          {/* Click Me Container */}
          <div className="mapClickMeContainer">
            <img
              className="mapClickMeIcon"
              src="/lightbulbs/lit.png"
              alt="Click me icon"
            />
            <p className="mapClickMeText">Tap each building to learn more!</p>
          </div>
        </div>
      </div>

      {/* last updated time */}
      <div
        style={{
          position: "absolute",
          bottom: "40px",
          right: "40px",
        }}
      >
        <span
          style={{
            textAnchor: "end",
            fontSize: 14,
            fontStyle: "italic",
            fontFamily: "National2",
          }}
        >
          Last Updated: {new Date(lastUpdated).toLocaleString("en-us")}
        </span>
      </div>
    </div>
  );
};
